import 'chartjs-adapter-luxon';

import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  DatePicker,
  FormField,
  SpaceBetween,
  Spinner,
  TimeInput,
} from '@cloudscape-design/components';
import { createVerticalLinePlugin } from 'components/ProcessData/chartShared';
import { CHANNEL_STATE_LABELS } from 'features/plc/contracts/testStand.schema';
import { useTheme } from 'features/theme/themeProvider';
import PropTypes from 'prop-types';
import React, {
  useCallback, useMemo, useState,
} from 'react';
import { Line } from 'react-chartjs-2';

import useTestStandProcessData from '../../hooks/useTestStandProcessData';

function toDatetimeLocal(ms) {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function parseDatePart(datetime) {
  if (!datetime) return '';
  return (datetime.split('T')[0] || '').replace(/-/g, '/');
}

function parseTimePart(datetime) {
  if (!datetime) return '';
  return (datetime.split('T')[1] || '').slice(0, 8);
}

function buildDatetime(datePart, timePart) {
  if (!datePart || !timePart) return '';
  return `${datePart.replace(/\//g, '-')}T${timePart}`;
}

const TODAY = new Date();
TODAY.setHours(23, 59, 59, 999);

function draftFromParams(params) {
  return {
    date: parseDatePart(params.startTime) || parseDatePart(params.endTime),
    startTimeValue: parseTimePart(params.startTime),
    endTimeValue: parseTimePart(params.endTime),
  };
}

const BASE_CHART_OPTIONS = {
  animation: false,
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      type: 'time',
      time: {
        displayFormats: {
          millisecond: 'HH:mm:ss.SSS',
          second: 'HH:mm:ss',
          minute: 'HH:mm',
          hour: 'HH:mm',
        },
      },
      ticks: { maxTicksLimit: 8, color: '#5f6b7a' },
      grid: { color: '#414d5c' },
    },
    y: {
      title: {
        display: true,
        text: 'Pressure (bar)',
        color: '#5f6b7a',
      },
      ticks: { color: '#5f6b7a' },
      grid: { color: '#414d5c' },
    },
  },
  plugins: {
    legend: {
      position: 'right',
      labels: { color: '#d1d5db', boxWidth: 12 },
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        title: (items) => {
          if (!items.length) return '';
          const ms = items[0].parsed.x;
          const d = new Date(ms);
          const pad = (n, w = 2) => String(n).padStart(w, '0');
          return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
        },
        afterBody: (items) => {
          const labels = [...new Set(
            items
              .map((item) => CHANNEL_STATE_LABELS[item.dataset.data[item.dataIndex]?.state])
              .filter(Boolean),
          )];
          return labels.map((l) => `State: ${l}`);
        },
      },
    },
  },
};

const SERIES = [
  {
    channelId: 'lipid',
    field: 'vesselPressure',
    label: 'Lipid vessel (bar)',
    color: '#ce3b26',
  },
  {
    channelId: 'lipid',
    field: 'pipetteTipPressure',
    label: 'Lipid pipette tip (bar)',
    color: '#0972d3',
  },
  {
    channelId: 'aqueous',
    field: 'vesselPressure',
    label: 'Aqueous vessel (bar)',
    color: '#e07941',
  },
  {
    channelId: 'aqueous',
    field: 'pipetteTipPressure',
    label: 'Aqueous pipette tip (bar)',
    color: '#00a4a4',
  },
];

function buildDatasets(rows) {
  return SERIES.map(({
    channelId, field, label, color,
  }) => ({
    label,
    data: rows
      .filter((r) => r.channelId === channelId)
      .map((r) => ({ x: r.time, y: r[field] ?? null, state: r.state })),
    borderColor: color,
    backgroundColor: 'transparent',
    pointRadius: 0,
    tension: 0.2,
  }));
}

export default function RunChart({ run = null }) {
  const { isDarkMode } = useTheme();

  const initParams = useMemo(() => {
    if (!run) return { startTime: '', endTime: '' };
    const startMs = run.startedAt ?? run.createdAt;
    const endMs = run.stoppedAt ?? run.updatedAt;
    const adjustedEndMs = endMs === startMs ? endMs + 60_000 : endMs;
    return {
      startTime: startMs ? toDatetimeLocal(startMs) : '',
      endTime: adjustedEndMs ? toDatetimeLocal(adjustedEndMs) : '',
    };
  }, [run]);
  const [draft, setDraft] = useState(() => draftFromParams(initParams));
  const [queryParams, setQueryParams] = useState(initParams);

  const setField = useCallback((patch) => setDraft((d) => ({ ...d, ...patch })), []);

  const {
    data: rows = [], isFetching, isError, error, isSuccess, handleExport,
  } = useTestStandProcessData(queryParams);

  const derivedStartTime = buildDatetime(draft.date, draft.startTimeValue);
  const derivedEndTime = buildDatetime(draft.date, draft.endTimeValue);
  const canQuery = Boolean(derivedStartTime && derivedEndTime);

  const handleSubmit = useCallback(() => {
    setQueryParams({
      startTime: derivedStartTime,
      endTime: derivedEndTime,
    });
  }, [derivedStartTime, derivedEndTime]);

  const chartOptions = useMemo(() => ({
    ...BASE_CHART_OPTIONS,
    plugins: {
      ...BASE_CHART_OPTIONS.plugins,
      verticalLine: {
        color: isDarkMode ? 'white' : 'black',
      },
    },
  }), [isDarkMode]);

  const verticalLinePlugin = useMemo(() => createVerticalLinePlugin(), []);

  const chartData = useMemo(() => ({
    datasets: buildDatasets(rows),
  }), [rows]);

  return (
    <SpaceBetween size="s">
      <ColumnLayout columns={3}>
        <FormField label="Date">
          <DatePicker
            value={draft.date}
            placeholder="YYYY/MM/DD"
            isDateEnabled={(d) => d <= TODAY}
            onChange={({ detail }) => setField({ date: detail.value })}
          />
        </FormField>
        <FormField label="Start time">
          <TimeInput
            value={draft.startTimeValue}
            format="hh:mm:ss"
            use24Hour
            placeholder="hh:mm:ss"
            onChange={({ detail }) => setField({ startTimeValue: detail.value })}
          />
        </FormField>
        <FormField label="End time">
          <TimeInput
            value={draft.endTimeValue}
            format="hh:mm:ss"
            use24Hour
            placeholder="hh:mm:ss"
            onChange={({ detail }) => setField({ endTimeValue: detail.value })}
          />
        </FormField>
      </ColumnLayout>
      <Box>
        <Button
          variant="primary"
          loading={isFetching}
          disabled={!canQuery}
          onClick={handleSubmit}
        >
          Query
        </Button>
        {rows.length > 0 && !isFetching && (
          <Box float="right" variant="span">
            <Button
              iconName="download"
              onClick={handleExport}
            >
              Export CSV
            </Button>
          </Box>
        )}
      </Box>
      {isFetching && (
        <Box textAlign="center" padding="m">
          <Spinner />
          <Box variant="p" color="text-status-inactive" padding={{ top: 'xs' }}>
            Querying Timestream…
          </Box>
        </Box>
      )}
      {isError && !isFetching && (
        <Alert type="error" header="Query failed">
          {error?.message}
        </Alert>
      )}
      {isSuccess && !isFetching && rows.length === 0 && (
        <Box color="text-status-inactive" textAlign="center" padding="m">
          No data found for this time range.
        </Box>
      )}
      {isSuccess && !isFetching && rows.length > 0 && (
        <div style={{ height: 280 }}>
          <Line data={chartData} options={chartOptions} plugins={[verticalLinePlugin]} />
        </div>
      )}
    </SpaceBetween>
  );
}

RunChart.propTypes = {
  run: PropTypes.shape({
    runId: PropTypes.string,
    testName: PropTypes.string,
    startedAt: PropTypes.number,
    createdAt: PropTypes.number,
    stoppedAt: PropTypes.number,
    updatedAt: PropTypes.number,
  }),
};
